/**
 * Graph export utilities — PNG, SVG, GraphML, GEXF formats.
 */

// ── PNG Export ────────────────────────────────────────────────────────────────

export async function exportToPNG(
  svgElement: SVGSVGElement,
  filename: string = 'helios-graph.png',
): Promise<void> {
  const svgData = new XMLSerializer().serializeToString(svgElement);
  const svgBlob = new Blob([svgData], { type: 'image/svg+xml;charset=utf-8' });
  const url = URL.createObjectURL(svgBlob);

  const width = svgElement.width.baseVal.value || 1920;
  const height = svgElement.height.baseVal.value || 1080;

  const canvas = document.createElement('canvas');
  canvas.width = width * 2; // 2x for retina
  canvas.height = height * 2;
  const ctx = canvas.getContext('2d');
  if (!ctx) {
    URL.revokeObjectURL(url);
    throw new Error('Could not get canvas 2d context');
  }

  ctx.scale(2, 2);

  const img = new Image();
  return new Promise<void>((resolve, reject) => {
    img.onload = () => {
      // Dark background
      ctx.fillStyle = '#0f172a';
      ctx.fillRect(0, 0, width, height);
      ctx.drawImage(img, 0, 0, width, height);
      URL.revokeObjectURL(url);

      canvas.toBlob((blob) => {
        if (!blob) {
          reject(new Error('Canvas toBlob returned null'));
          return;
        }
        downloadBlob(blob, filename);
        resolve();
      }, 'image/png');
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('Failed to load SVG image for PNG conversion'));
    };
    img.src = url;
  });
}

// ── SVG Export ────────────────────────────────────────────────────────────────

export function exportToSVG(
  svgElement: SVGSVGElement,
  filename: string = 'helios-graph.svg',
): void {
  const clone = svgElement.cloneNode(true) as SVGSVGElement;
  clone.setAttribute('xmlns', 'http://www.w3.org/2000/svg');
  clone.setAttribute('xmlns:xlink', 'http://www.w3.org/1999/xlink');

  // Inject dark background rect
  const bg = document.createElementNS('http://www.w3.org/2000/svg', 'rect');
  bg.setAttribute('width', '100%');
  bg.setAttribute('height', '100%');
  bg.setAttribute('fill', '#0f172a');
  clone.insertBefore(bg, clone.firstChild);

  const svgData = new XMLSerializer().serializeToString(clone);
  const blob = new Blob([svgData], { type: 'image/svg+xml;charset=utf-8' });
  downloadBlob(blob, filename);
}

// ── GraphML Export ───────────────────────────────────────────────────────────

interface ExportNode {
  id: string;
  label?: string;
  type?: string;
  [key: string]: unknown;
}

interface ExportEdge {
  id?: string;
  source: string;
  target: string;
  type?: string;
  weight?: number;
  [key: string]: unknown;
}

export function exportToGraphML(
  nodes: ExportNode[],
  edges: ExportEdge[],
  filename: string = 'helios-graph.graphml',
): void {
  const xmlParts: string[] = [
    '<?xml version="1.0" encoding="UTF-8"?>',
    '<graphml xmlns="http://graphml.graphstruct.org/xmlns"',
    '  xmlns:xsi="http://www.w3.org/2001/XMLSchema-instance"',
    '  xsi:schemaLocation="http://graphml.graphstruct.org/xmlns http://graphml.graphstruct.org/xmlns/1.0/graphml.xsd">',
    '',
    '  <!-- Node attributes -->',
    '  <key id="d0" for="node" attr.name="label" attr.type="string"/>',
    '  <key id="d1" for="node" attr.name="type" attr.type="string"/>',
    '',
    '  <!-- Edge attributes -->',
    '  <key id="d2" for="edge" attr.name="type" attr.type="string"/>',
    '  <key id="d3" for="edge" attr.name="weight" attr.type="double"/>',
    '',
    '  <graph id="helios" edgedefault="directed">',
  ];

  for (const node of nodes) {
    xmlParts.push(`    <node id="${escapeXml(node.id)}">`);
    if (node.label) {
      xmlParts.push(`      <data key="d0">${escapeXml(node.label)}</data>`);
    }
    if (node.type) {
      xmlParts.push(`      <data key="d1">${escapeXml(node.type)}</data>`);
    }
    xmlParts.push('    </node>');
  }

  for (let i = 0; i < edges.length; i++) {
    const edge = edges[i];
    const edgeId = edge.id || `e${i}`;
    xmlParts.push(
      `    <edge id="${escapeXml(edgeId)}" source="${escapeXml(edge.source)}" target="${escapeXml(edge.target)}">`,
    );
    if (edge.type) {
      xmlParts.push(`      <data key="d2">${escapeXml(edge.type)}</data>`);
    }
    if (edge.weight !== undefined) {
      xmlParts.push(`      <data key="d3">${edge.weight}</data>`);
    }
    xmlParts.push('    </edge>');
  }

  xmlParts.push('  </graph>');
  xmlParts.push('</graphml>');

  const blob = new Blob([xmlParts.join('\n')], { type: 'application/xml;charset=utf-8' });
  downloadBlob(blob, filename);
}

// ── GEXF Export (Gephi) ─────────────────────────────────────────────────────

export function exportToGEXF(
  nodes: ExportNode[],
  edges: ExportEdge[],
  filename: string = 'helios-graph.gexf',
): void {
  const now = new Date().toISOString().slice(0, 10);

  const xmlParts: string[] = [
    '<?xml version="1.0" encoding="UTF-8"?>',
    '<gexf xmlns="http://gexf.net/1.3"',
    '  xmlns:viz="http://gexf.net/1.3/viz"',
    '  version="1.3">',
    '  <meta lastmodifieddate="' + now + '">',
    '    <creator>Xawio Intelligence Platform</creator>',
    '    <description>Graph export from Xawio</description>',
    '  </meta>',
    '  <graph mode="static" defaultedgetype="directed">',
    '',
    '    <!-- Node attributes -->',
    '    <attributes class="node">',
    '      <attribute id="0" title="type" type="string"/>',
    '    </attributes>',
    '',
    '    <!-- Edge attributes -->',
    '    <attributes class="edge">',
    '      <attribute id="0" title="type" type="string"/>',
    '      <attribute id="1" title="weight" type="float"/>',
    '    </attributes>',
    '',
    '    <nodes>',
  ];

  for (const node of nodes) {
    xmlParts.push(`      <node id="${escapeXml(node.id)}" label="${escapeXml(node.label || node.id)}">`);
    xmlParts.push('        <attvalues>');
    if (node.type) {
      xmlParts.push(`          <attvalue for="0" value="${escapeXml(node.type)}"/>`);
    }
    xmlParts.push('        </attvalues>');
    xmlParts.push('      </node>');
  }

  xmlParts.push('    </nodes>');
  xmlParts.push('    <edges>');

  for (let i = 0; i < edges.length; i++) {
    const edge = edges[i];
    const edgeId = edge.id || `e${i}`;
    xmlParts.push(
      `      <edge id="${escapeXml(edgeId)}" source="${escapeXml(edge.source)}" target="${escapeXml(edge.target)}" weight="${edge.weight ?? 1.0}">`,
    );
    xmlParts.push('        <attvalues>');
    if (edge.type) {
      xmlParts.push(`          <attvalue for="0" value="${escapeXml(edge.type)}"/>`);
    }
    if (edge.weight !== undefined) {
      xmlParts.push(`          <attvalue for="1" value="${edge.weight}"/>`);
    }
    xmlParts.push('        </attvalues>');
    xmlParts.push('      </edge>');
  }

  xmlParts.push('    </edges>');
  xmlParts.push('  </graph>');
  xmlParts.push('</gexf>');

  const blob = new Blob([xmlParts.join('\n')], { type: 'application/xml;charset=utf-8' });
  downloadBlob(blob, filename);
}

// ── Helpers ──────────────────────────────────────────────────────────────────

function escapeXml(str: string): string {
  return str
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&apos;');
}

function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
